import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  X,
  RefreshCw,
  Filter,
  Search,
  Calendar,
  Users,
  MessageSquare,
  CheckCheck
} from 'lucide-react';
import { useApi } from '../utils/useApi';
import { useAuth } from '../contexts/AuthContext';

interface Campaign {
  id: string;
  gymId: string;
  name: string | null;
  templateId: string;
  status: string;
  scheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  template: {
    id: string;
    title: string;
    templateName: string;
    content: string;
    parameterSchema?: Record<string, any> | null;
  } | null;
  gym?: { id: string; name: string } | null;
  messageParams?: Record<string, any> | null;
  stats?: Record<string, number>;
  recipients?: Array<{
    id: string;
    memberId: string | null;
    phoneNumber: string;
    status: string;
    sentAt: string | null;
    deliveredAt: string | null;
    failedReason: string | null;
    member: {
      id: string;
      fullName: string;
      displayName: string | null;
      phoneNumber: string;
      email: string | null;
    } | null;
  }>;
  // Legacy fields for backward compatibility
  label?: string;
  campaignUuid?: string;
  tenantId?: string;
  totalRecipients?: number;
  excludedOptOut?: number;
  sentCount?: number;
  failedCount?: number;
  messageCounts?: Record<string, number>;
}

interface CampaignMessage {
  id: string;
  campaignId: string;
  memberId: string | null;
  phoneNumber: string;
  messageStatus: string;
  failedReason: string | null;
  costPerMessage: string | number | null;
  totalCost: string | number | null;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  member: { id: string; fullName: string | null; phoneNumber: string } | null;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  READY: 'bg-blue-100 text-blue-800',
  SENDING: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_SENT: 'bg-orange-100 text-orange-800',
  SENT: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  DELIVERED: 'bg-green-100 text-green-800',
  READ: 'bg-blue-100 text-blue-800',
  // Enhanced campaign statuses (lowercase)
  draft: 'bg-gray-100 text-gray-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  sending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-gray-100 text-gray-800', // Changed sent to gray/neutral as it's intermediate
  delivered: 'bg-green-100 text-green-800',
  read: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
  deleted: 'bg-gray-100 text-gray-800'
};

const STATUS_ICONS: Record<string, any> = {
  DRAFT: Clock,
  READY: Clock,
  SENDING: RefreshCw,
  PARTIALLY_SENT: AlertCircle,
  SENT: CheckCircle,
  FAILED: XCircle,
  CANCELLED: X,
  DELIVERED: CheckCircle,
  READ: CheckCheck,
  // Enhanced campaign statuses
  draft: Clock,
  pending_approval: AlertCircle,
  approved: CheckCircle,
  scheduled: Calendar,
  sending: RefreshCw,
  sent: CheckCircle,
  delivered: CheckCircle,
  read: CheckCheck,
  failed: XCircle,
  cancelled: X,
  deleted: X
};

const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status] || STATUS_COLORS[status.toUpperCase()] || 'bg-gray-100 text-gray-800';
};

const getStatusIcon = (status: string) => {
  const Icon = STATUS_ICONS[status] || STATUS_ICONS[status.toUpperCase()] || Clock;
  return <Icon className="h-3 w-3" />;
};

// Delivered/read messages were sent too; legacy campaigns carry sentCount instead of stats.
const sentMessages = (campaign: Campaign): number =>
  campaign.stats
    ? (campaign.stats.sent || 0) + (campaign.stats.delivered || 0) + (campaign.stats.read || 0)
    : campaign.sentCount || 0;

/**
 * Reconstruct the message a campaign sends, mirroring the server's
 * resolveParamValue: user-typed params come from the campaign's stored
 * messageParams, gym params from the gym name, and per-recipient member
 * params are shown as bracketed placeholders since they vary per member.
 */
const renderActualMessage = (campaign: Campaign): string => {
  const template = campaign.template;
  if (!template?.content) return '';
  let text = template.content;
  const schema = template.parameterSchema || {};
  const params = campaign.messageParams || {};
  Object.entries(schema)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .forEach(([schemaKey, param]: [string, any]) => {
      let value = '';
      switch (param?.source) {
        case 'gym_data':
          value = campaign.gym?.name || 'Your gym';
          break;
        case 'member_data':
          value = `[${param.description || param.key || 'Member'}]`;
          break;
        case 'static':
          value = param.value || '';
          break;
        default:
          value = String(params[schemaKey] ?? params[param?.key] ?? '').trim().replace(/[.!?,;:]+$/, '');
          break;
      }
      text = text.replace(new RegExp(`\\{\\{${schemaKey}\\}\\}`, 'g'), value);
    });
  return text;
};

interface CampaignsPageProps {
  gymId: string;
  hideHeader?: boolean;
}

export default function CampaignsPage({ gymId: _gymId, hideHeader = false }: CampaignsPageProps) {
  // TODO: Use gymId for tenant-specific operations
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [messageFilter, setMessageFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showMessages, setShowMessages] = useState<boolean>(false);
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false);
  const [campaignToSchedule, setCampaignToSchedule] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');

  const { fetchWithAuth } = useApi();
  const { user } = useAuth();

  // Get current user's gym ID (tenant ID)
  const { data: userData } = useQuery({
    queryKey: ['user-context'],
    queryFn: async () => {
      return await fetchWithAuth('/api/auth/context');
    }
  });

  const tenantId = userData?.data?.accessibleGyms?.[0];

  // Fetch campaigns
  const { data: campaignsData, refetch: refetchCampaigns, isFetching: isFetchingCampaigns } = useQuery({
    queryKey: ['enhanced-campaigns', tenantId],
    queryFn: async () => {
      if (!tenantId) return { data: [] };
      return await fetchWithAuth(`/api/enhanced-campaigns?gymId=${tenantId}&limit=50`);
    },
    enabled: !!tenantId
  });

  // Fetch campaign messages
  const { data: messagesData } = useQuery({
    queryKey: ['enhanced-campaign-messages', selectedCampaign?.id, messageFilter],
    queryFn: async () => {
      if (!selectedCampaign?.id || !tenantId) return { data: [] };
      const statusParam = messageFilter !== 'all' ? `&status=${messageFilter}` : '';
      return await fetchWithAuth(`/api/enhanced-campaigns/${selectedCampaign.id}/messages?gymId=${tenantId}${statusParam}&limit=100`);
    },
    enabled: !!selectedCampaign?.id && !!tenantId
  });

  const campaigns: Campaign[] = campaignsData?.data || [];
  const messages: CampaignMessage[] = messagesData?.data || [];

  const filteredCampaigns = campaigns.filter(campaign => {
    const searchLower = searchTerm.toLowerCase();
    const campaignName = (campaign.name || campaign.label || '').toLowerCase();
    const templateTitle = campaign.template?.title?.toLowerCase() || '';
    const status = (campaign.status || '').toLowerCase();

    return campaignName.includes(searchLower) ||
      templateTitle.includes(searchLower) ||
      status.includes(searchLower);
  });

  const handleViewCampaign = async (campaign: Campaign) => {
    try {
      // Fetch full campaign details with recipients
      const response = await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}`);
      if (response.success) {
        setSelectedCampaign(response.data);
        setShowMessages(true);
      }
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      alert('Failed to load campaign details');
    }
  };

  const handleCancelCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;

    try {
      await fetchWithAuth(`/api/enhanced-campaigns/${campaignId}/cancel`, {
        method: 'POST'
      });
      refetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign(null);
        setShowMessages(false);
      }
      alert('Campaign deleted successfully');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const handleApproveCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to approve this campaign?')) return;

    try {
      await fetchWithAuth(`/api/enhanced-campaigns/${campaignId}/approve`, {
        method: 'POST'
      });
      refetchCampaigns();
      alert('Campaign approved successfully!');
    } catch (error) {
      console.error('Error approving campaign:', error);
      alert('Failed to approve campaign');
    }
  };

  const handleScheduleCampaign = (campaignId: string) => {
    setCampaignToSchedule(campaignId);
    setShowScheduleModal(true);
    // Set default date/time to tomorrow at 10 AM
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);

    const dateStr = tomorrow.toISOString().split('T')[0];
    const timeStr = '10:00';
    setScheduleDate(dateStr);
    setScheduleTime(timeStr);
  };

  const handleConfirmSchedule = async () => {
    if (!campaignToSchedule || !scheduleDate || !scheduleTime) {
      alert('Please select both date and time');
      return;
    }

    // Combine date and time
    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);

    // Validate it's in the future
    if (scheduledDateTime < new Date()) {
      alert('Please select a date and time in the future');
      return;
    }

    try {
      await fetchWithAuth(`/api/enhanced-campaigns/${campaignToSchedule}/schedule`, {
        method: 'POST',
        body: JSON.stringify({
          scheduled_at: scheduledDateTime.toISOString(),
          scheduled_by: user?.id
        })
      });
      refetchCampaigns();
      setShowScheduleModal(false);
      setCampaignToSchedule(null);
      setScheduleDate('');
      setScheduleTime('');
      alert('Campaign scheduled successfully!');
    } catch (error) {
      console.error('Error scheduling campaign:', error);
      alert('Failed to schedule campaign');
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
            <p className="text-gray-600">View and manage your WhatsApp campaigns</p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4" />
            Back
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Filter:</span>
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">All Campaigns</h3>
          <button
            onClick={() => refetchCampaigns()}
            disabled={isFetchingCampaigns}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors disabled:opacity-50"
            title="Refresh campaigns and logs"
          >
            <RefreshCw className={`h-4 w-4 ${isFetchingCampaigns ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {filteredCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'No campaigns match your search criteria.' : 'Create your first campaign to get started.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCampaigns.map((campaign) => (
              <div key={campaign.id} className="p-4 sm:p-6 hover:bg-gray-50 border-b border-gray-200 last:border-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 truncate">{campaign.name || campaign.label || 'Unnamed Campaign'}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider ${getStatusColor(campaign.status || '')}`}>
                        {getStatusIcon(campaign.status || '')}
                        {campaign.status || 'unknown'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 sm:gap-6 text-xs sm:text-sm text-gray-500 mb-3 font-medium">
                      <div className="flex items-center gap-1.5">
                        <MessageSquare className="h-3.5 w-3.5 text-blue-500" />
                        <span className="truncate">{campaign.template?.title || 'No template'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-purple-500" />
                        <span>{campaign.stats?.total || campaign.totalRecipients || 0} recipients</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-orange-500" />
                        <span className="whitespace-nowrap">{new Date(campaign.createdAt).toLocaleDateString()}</span>
                      </div>
                      {campaign.status === 'scheduled' && campaign.scheduledAt && (
                        <div
                          className="flex items-center gap-1.5 text-purple-700"
                          title="Scheduled campaigns are picked up within ~5 minutes of this time"
                        >
                          <Clock className="h-3.5 w-3.5 text-purple-500" />
                          <span className="whitespace-nowrap">Runs {new Date(campaign.scheduledAt).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] sm:text-xs font-bold uppercase">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-green-700">{sentMessages(campaign)} sent</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-red-700">{campaign.stats?.failed || campaign.failedCount || 0} failed</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:self-center border-t sm:border-t-0 pt-3 sm:pt-0">
                    <button
                      onClick={() => handleViewCampaign(campaign)}
                      className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-100"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>

                    {/* Enhanced campaign actions */}
                    {campaign.status === 'draft' && (
                      <button
                        onClick={() => {
                          fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/submit`, {
                            method: 'POST',
                            body: JSON.stringify({
                              submitted_by: user?.id
                            })
                          }).then(() => {
                            refetchCampaigns();
                            alert('Campaign submitted for approval');
                          }).catch(error => {
                            console.error('Error submitting campaign:', error);
                            alert('Failed to submit campaign');
                          });
                        }}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-100 transition-colors"
                      >
                        Submit
                      </button>
                    )}
                    {campaign.status === 'pending_approval' && (
                      <button
                        onClick={() => handleApproveCampaign(campaign.id)}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-100 transition-colors"
                      >
                        Approve
                      </button>
                    )}
                    {campaign.status === 'approved' && (
                      <button
                        onClick={() => handleScheduleCampaign(campaign.id)}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-100 transition-colors"
                      >
                        Schedule
                      </button>
                    )}
                    {/* Cancel button */}
                    {(campaign.status === 'scheduled' || campaign.status === 'approved' || campaign.status === 'pending_approval' || campaign.status === 'draft' || campaign.status === 'DRAFT' || campaign.status === 'READY' || campaign.status === 'SENDING') && (
                      <button
                        onClick={() => handleCancelCampaign(campaign.id)}
                        className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 transition-colors"
                        title="Cancel/Delete"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      {showMessages && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedCampaign.name || selectedCampaign.label || 'Campaign Details'}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedCampaign.recipients?.length || selectedCampaign.totalRecipients || 0} recipients •
                  {selectedCampaign.template?.title || 'No template'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMessages(false);
                  setSelectedCampaign(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Campaign Details Section */}
              <div className="p-6 border-b border-gray-200">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Campaign Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCampaign.status || '')}`}>
                        {getStatusIcon(selectedCampaign.status || '')}
                        {selectedCampaign.status || 'unknown'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Template</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedCampaign.template?.title || 'N/A'}
                    </p>
                  </div>
                  {selectedCampaign.scheduledAt && (
                    <div>
                      <label className="text-sm text-gray-600">
                        {selectedCampaign.status === 'scheduled' ? 'Runs At' : 'Scheduled At'}
                      </label>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatDate(selectedCampaign.scheduledAt)}
                      </p>
                      {selectedCampaign.status === 'scheduled' && (
                        <p className="text-xs text-gray-500">Picked up within ~5 minutes of this time</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-gray-600">Created At</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {formatDate(selectedCampaign.createdAt)}
                    </p>
                  </div>
                  {selectedCampaign.approvedAt && (
                    <div>
                      <label className="text-sm text-gray-600">Approved At</label>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatDate(selectedCampaign.approvedAt)}
                      </p>
                    </div>
                  )}
                  {selectedCampaign.stats && (
                    <>
                      <div>
                        <label className="text-sm text-gray-600">Total Messages</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {selectedCampaign.stats.total || 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Sent</label>
                        <p className="mt-1 text-sm font-medium text-green-600">
                          {sentMessages(selectedCampaign)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Failed</label>
                        <p className="mt-1 text-sm font-medium text-red-600">
                          {selectedCampaign.stats.failed || 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Pending</label>
                        <p className="mt-1 text-sm font-medium text-yellow-600">
                          {selectedCampaign.stats.pending || 0}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Actual message as sent to recipients */}
                {selectedCampaign.template?.content && (
                  <div className="mt-6">
                    <label className="text-sm text-gray-600">Message</label>
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">
                      {renderActualMessage(selectedCampaign)}
                    </div>
                  </div>
                )}
              </div>

              {/* Qualified Users Section */}
              <div className="p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">
                  Qualified Users ({selectedCampaign.recipients?.length || 0})
                </h4>
                {selectedCampaign.recipients && selectedCampaign.recipients.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {selectedCampaign.recipients.map((recipient) => (
                      <div key={recipient.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                            <Users className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {recipient.member?.displayName || recipient.member?.fullName || 'Unknown Member'}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>{recipient.phoneNumber}</span>
                              {recipient.member?.email && (
                                <span>{recipient.member.email}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-end gap-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(recipient.status || '')}`}>
                              {getStatusIcon(recipient.status || '')}
                              {recipient.status || 'unknown'}
                            </span>
                            {recipient.status.toUpperCase() === 'FAILED' && recipient.failedReason === 'Insufficient message credits' && (
                              <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded border border-red-100 font-bold uppercase tracking-wider">
                                No credits
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No recipients found for this campaign.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )
      }

      {/* Legacy Campaign Messages Modal */}
      {
        showMessages && selectedCampaign && messages.length > 0 && !selectedCampaign.recipients && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6">
                {/* Message Filter */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Filter by status:</span>
                  </div>
                  <select
                    value={messageFilter}
                    onChange={(e) => setMessageFilter(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="all">All Messages</option>
                    <option value="pending">Pending</option>
                    <option value="sending">Sending</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                {/* Messages List */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No messages found for this filter.
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div key={message.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <span className="font-medium text-gray-900">{message.member?.fullName || message.phoneNumber}</span>
                              {message.member?.fullName && (
                                <span className="text-xs text-gray-400 ml-2 font-mono">{message.phoneNumber}</span>
                              )}
                            </div>
                            <div className="flex flex-col items-start gap-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.messageStatus || '')}`}>
                                {getStatusIcon(message.messageStatus || '')}
                                {message.messageStatus || 'unknown'}
                              </span>
                              {message.messageStatus === 'failed' && message.failedReason === 'Insufficient message credits' && (
                                <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded border border-red-100 font-bold uppercase tracking-wider">
                                  No credits
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">
                              {formatDate(message.createdAt)}
                            </div>
                            <div className="text-sm font-bold text-gray-900">₹{Number(message.totalCost || 0).toFixed(2)}</div>
                            {message.costPerMessage != null && Number(message.costPerMessage) > 0 && (
                              <div className="text-[10px] text-gray-500">₹{Number(message.costPerMessage).toFixed(2)}/msg</div>
                            )}
                          </div>
                        </div>

                        {message.failedReason && (
                          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            Error: {message.failedReason}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Schedule Campaign Modal */}
      {
        showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Schedule Campaign</h3>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setCampaignToSchedule(null);
                    setScheduleDate('');
                    setScheduleTime('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Date
                  </label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Time
                  </label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {scheduleDate && scheduleTime && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        Scheduled for: <span className="font-medium text-gray-900">
                          {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}
                        </span>
                      </p>
                      {new Date(`${scheduleDate}T${scheduleTime}`) < new Date() && (
                        <p className="text-sm text-red-600 mt-1">⚠️ Selected time is in the past</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowScheduleModal(false);
                      setCampaignToSchedule(null);
                      setScheduleDate('');
                      setScheduleTime('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSchedule}
                    disabled={!scheduleDate || !scheduleTime || (scheduleDate && scheduleTime ? new Date(`${scheduleDate}T${scheduleTime}`) < new Date() : true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Schedule Campaign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
